import { toError } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

export const redeemReferralCodeRequestSchema = z.object({
  referralCode: z.string(),
});

export const redeemReferralCodeResponseSchema = z.object({
  referralCodeValid: z.boolean(),
  message: z.string(),
  bonusAmount: z.number().optional(),
  resultType: z.string().optional(),
});

export type RedeemReferralCodeRequest = z.infer<typeof redeemReferralCodeRequestSchema>;
export type RedeemReferralCodeResponse = z.infer<typeof redeemReferralCodeResponseSchema>;

interface RedeemReferralCodeParams {
  referralCode: string;
}

export function useRedeemReferralCode(
  options: UseMutationOptions<RedeemReferralCodeResponse, Error, RedeemReferralCodeParams> = {}
) {
  return useMutation<RedeemReferralCodeResponse, Error, RedeemReferralCodeParams>(
    async (params) => {
      const { referralCode } = params;

      try {
        const response = await post({
          url: `${environmentConfig.REACT_APP_BASE_API_URL}/agentprofile/referral`,
          data: { referralCode },
          requestSchema: redeemReferralCodeRequestSchema,
          responseSchema: redeemReferralCodeResponseSchema,
        });

        return response.data;
      } catch (error: any) {
        // Handle case where API returns 400 but with valid response body
        if (error?.response?.status === 400 && error?.response?.data) {
          try {
            // Try to parse the response data using our schema
            return redeemReferralCodeResponseSchema.parse(error.response.data);
          } catch {
            // If parsing fails, fall through to throw the original error
          }
        }

        throw toError(error);
      }
    },
    {
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.REFERRAL_CODE_REDEEM_FAILURE,
        userErrorMessage: "Failed to redeem referral code",
      },
      ...options,
    }
  );
}
